import { Link } from 'react-router-dom'
import { ArrowRight, ClipboardList } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { STATUS_BADGE, TYPE_LABEL } from '@/components/requests/requests-workspace'
import type { ServiceRequest } from '@riverpark/types'

const PANEL_LIMIT = 5

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function OpenRequestsPanel({
  basePath,
  requests,
  isLoading,
}: {
  basePath: string
  requests: ServiceRequest[]
  isLoading: boolean
}) {
  const shown = requests.slice(0, PANEL_LIMIT)

  return (
    <div className="mb-6 rounded-card border border-border-2 bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border-2 px-5 py-3">
        <div className="flex items-center gap-2">
          <ClipboardList size={16} className="text-muted-foreground" />
          <h2 className="font-serif text-lg text-ink">Open service requests</h2>
        </div>
        <Link
          to={`${basePath}/requests`}
          className="flex items-center gap-1 font-sans text-xs font-medium text-muted-foreground hover:text-primary"
        >
          View all
          <ArrowRight size={12} />
        </Link>
      </div>

      {isLoading ? (
        <p className="px-5 py-6 font-sans text-sm text-muted-foreground">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="px-5 py-6 font-sans text-sm text-muted-foreground">
          No open service requests.
        </p>
      ) : (
        <ul className="divide-y divide-border-2">
          {shown.map((r) => (
            <li key={r.id}>
              <Link
                to={`${basePath}/requests?open=${r.id}`}
                className="block px-5 py-3.5 transition-colors hover:bg-cream-2"
              >
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_BADGE[r.status].variant}>
                    {STATUS_BADGE[r.status].label}
                  </Badge>
                  <p className="truncate font-sans text-sm font-medium text-ink">{r.subject}</p>
                </div>
                <p className="mt-1 font-sans text-xs text-muted-foreground">
                  {TYPE_LABEL[r.type]} · {r.memberName ?? r.reporterName ?? 'Public report'}
                  {r.plotNumber && <> · Plot {r.plotNumber}</>} · {formatDateTime(r.createdAt)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
